/**
 * SalesApprovalsPage — Phase 33.10.
 *
 * Unified approval queue for outreach and proposal items awaiting
 * founder review. Polls every 30 s; urgent items (pending >48 h)
 * get a red left border.
 */

import { useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ApprovalItem {
  item_id: string;
  item_type: string;
  lead_id: string;
  subject: string;
  preview_snippet: string;
  status: string;
  queued_at: number;
  decided_at: number | null;
  rejection_reason: string | null;
  is_urgent: boolean;
}

interface ApprovalsResponse {
  items: ApprovalItem[];
  total: number;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

const APPROVAL_STATUS_COLORS: Record<string, string> = {
  pending:  '#fbbf24',
  approved: '#34d399',
  rejected: '#f87171',
  draft:    '#7dd3fc',
};

export function statusColor(s: string): string {
  return APPROVAL_STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

export function fmtQueued(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function urgencyLabel(item: Pick<ApprovalItem, 'is_urgent' | 'status'>): string {
  if (item.is_urgent && item.status === 'pending') return '⚠ Urgent';
  return '';
}

export function itemTypeLabel(item_type: string): string {
  if (item_type === 'outreach') return 'Email';
  if (item_type === 'proposal') return 'Proposal';
  return item_type;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchQueue(type: string, status: string): Promise<ApprovalsResponse> {
  const token = await getAccessToken();
  const params = new URLSearchParams();
  if (type)   params.set('type',   type);
  if (status) params.set('status', status);
  const resp = await fetch(`${apiBaseUrl()}/sales/approvals/queue?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<ApprovalsResponse>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4,
  color: 'var(--omega-ink)',
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  padding: '4px 8px',
  outline: 'none',
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesApprovalsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  const { data, isPending, isError } = useQuery({
    queryKey: ['sales', 'approvals', 'queue', typeFilter, statusFilter],
    queryFn: () => fetchQueue(typeFilter, statusFilter),
    refetchInterval: 30_000,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA',  to: '/omega' },
          { label: 'Sales',  to: '/omega/sales' },
          { label: 'Approval queue' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          Approval queue
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {items.length}
          </span>
        )}
        <Link
          to="/omega/sales/approvals/history"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4,
            color: '#7dd3fc',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
            letterSpacing: '0.04em',
          }}
        >
          History →
        </Link>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={inputStyle}>
          <option value="">All</option>
          <option value="outreach">Emails</option>
          <option value="proposal">Proposals</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
          <option value="pending">Pending</option>
          <option value="">All</option>
        </select>
      </div>

      {/* Column headers */}
      {!isPending && !isError && items.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 1fr 120px 130px 90px 72px',
          gap: 10,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Type', 'Subject', 'Lead', 'Arrived', 'Status', 'Action'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', direction: 'ltr' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading queue.</div>}

      {!isPending && !isError && items.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center', direction: 'ltr' }}>
          No items pending approval — all handled!
        </div>
      )}

      {items.map(item => (
        <div
          key={item.item_id}
          style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 120px 130px 90px 72px',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            borderLeft: item.is_urgent && item.status === 'pending'
              ? '3px solid #f87171'
              : '3px solid transparent',
          }}
        >
          <span style={{
            fontSize: '7.5pt',
            padding: '1px 6px',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'var(--omega-ink-subtle)',
            borderRadius: 3,
            textAlign: 'center',
            direction: 'ltr',
          }}>
            {itemTypeLabel(item.item_type)}
          </span>

          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '9.5pt', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
              {urgencyLabel(item) && (
                <span style={{ color: '#f87171', marginLeft: 6, fontSize: '8pt' }}>{urgencyLabel(item)}</span>
              )}
              {item.subject}
            </div>
            {item.preview_snippet && (
              <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
                {item.preview_snippet}
              </div>
            )}
          </div>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.lead_id}
          </span>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {fmtQueued(item.queued_at)}
          </span>

          <span style={{
            fontSize: '7.5pt',
            padding: '1px 6px',
            border: `1px solid ${statusColor(item.status)}`,
            color: statusColor(item.status),
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            borderRadius: 3,
            justifySelf: 'start',
          }}>
            {item.status}
          </span>

          <Link
            to={`/omega/sales/approvals/${item.item_id}`}
            style={{
              background: 'rgba(251,191,36,0.12)',
              border: '1px solid rgba(251,191,36,0.35)',
              borderRadius: 4,
              color: '#fbbf24',
              fontFamily: 'var(--omega-mono)',
              fontSize: '8pt',
              fontWeight: 600,
              padding: '4px 10px',
              textDecoration: 'none',
              direction: 'ltr',
              whiteSpace: 'nowrap',
            }}
          >
            Review
          </Link>
        </div>
      ))}
    </div>
  );
}
